import { Outlet } from "react-router";
import { LogoutButton, NavbarLink } from "./NavComponents";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { useEffect, useState } from "react";
import {
  generalDataAtom,
  hostDataAtom,
  loginStatusAtom,
  modelDataAtom,
  themeDataAtom,
  userAtom,
} from "../atoms";
import apiAgent from "../api/apiAgent";
import { transformModelSchema } from "../utils";
import { IModelData } from "../atoms";
import { Loader } from "../components/Loader";
import { checkSingleItemSchemas } from "../utils/singleItemsConfig";
import { PublishBanner } from "../components/Publishing";
import { getPublishStates } from "../components/Publishing/utils";
import { publishGroups } from "../components/Publishing/publishConfig";
import { touchwallPublishAtom } from "../components/Publishing/atoms";

/**
 * Modify this fo adjust menu items
 * @returns Navbar Menu
 */

const NavbarLayout = () => {
  const setModelData = useSetAtom(modelDataAtom);
  const setHostData = useSetAtom(hostDataAtom);
  const generalData = useAtomValue(generalDataAtom);
  const user = useAtomValue(userAtom);
  const [loginStatus, setLoginStatus] = useAtom(loginStatusAtom);
  const [modelsLoaded, setModelsLoaded] = useState<boolean>(false);
  const [hostDataLoaded, setHostDataLoaded] = useState<boolean>(false);
  const setThemeData = useSetAtom(themeDataAtom);
  const setTWPublishStates = useSetAtom(touchwallPublishAtom);

  useEffect(() => {
    if (loginStatus === true) {
      if (!modelsLoaded) fetchModelData();
      if (!hostDataLoaded) fetchHostData();
      fetchThemeData();
      checkSingleItemSchemas();
      // TODO: Get publish states here like this:
      getPublishStates("touchwall", setTWPublishStates);
    }
  }, [loginStatus]);

  const fetchHostData = async () => {
    try {
      const response = await apiAgent.Hosting.get();
      // console.log(hostResponse);
      if (response.status === 200) {
        // console.log("Host data loaded");
        setHostData(response.data);
        setHostDataLoaded(true);
      } else if (response.status === 401) {
        setLoginStatus(false);
      }
    } catch (err) {
      console.log("Could not set host data", err);
    }
  };

  const fetchModelData = async () => {
    try {
      const response = await apiAgent.Models.getAll();
      // console.log(response.data);
      if (response.status === 200) {
        // Since the CMS also allows managing "users" (a system model), add that here too
        const userModel: IModelData = response.data.system_models.find(
          (sm: any) => sm.name === "users",
        );
        const allModels: IModelData[] = [userModel, ...response.data.models];

        const modelData = transformModelSchema(allModels);
        // console.log("Model data loaded:", modelData);
        setModelData(modelData);
        setModelsLoaded(true);
      } else if (response.status === 401) {
        setLoginStatus(false);
      }
    } catch (err) {
      console.log(err);
      console.log("Could not load modelData");
    }
  };

  const fetchThemeData = async () => {
    try {
      const response = await apiAgent.DataType.getAll("themes");
      setThemeData(response.data);
    } catch (err) {
      console.log(err);
    }
  };

  return (
    <div className="w-full h-full min-w-[60rem] flex bg-sidebarBg">
      <div className="w-[16rem] flex flex-col">
        <div className="p-8 w-full flex justify-center">
          <img src="/assets/logo-transparent.png" className="w-24" />
        </div>
        <PublishBanner appGroup="touchwall" />
        <NavbarLink to="/">Home</NavbarLink>
        <NavbarLink to="/m/stories">Stories</NavbarLink>
        <NavbarLink to="/m/themes">Themes</NavbarLink>
        <NavbarLink to="/m/acknowledgements">Acknowledgements</NavbarLink>
        <NavbarLink to="/m/links">Links</NavbarLink>
        {/* TODO: OPTIONAL, enable access to users menu here */}
        {user?.user_role === "admin" && generalData.enable_user_management && (
          <NavbarLink to="/m/users">Manage Users</NavbarLink>
        )}
        {/* TODO: Add other sidebar links here, example: */}
        {/* <NavbarLink to="/m/model">Model</NavbarLink> */}
        <div className="w-full flex-grow " />
        <div className="w-full text-center text-sm pb-2">
          Logged in as <span className="font-bold">{user?.username}</span>
        </div>
        <LogoutButton />
      </div>
      <div className="w-[calc(100%-16rem)] h-full bg-white text-black">
        {modelsLoaded ? (
          <Outlet />
        ) : (
          <div className="w-full h-full text-center grid content-center text-grayText">
            <Loader />
            <div className="my-4">loading model data... Please wait</div>
          </div>
        )}
      </div>
    </div>
  );
};

export { NavbarLayout };
